import RoundStatus from '@/roundStatus';
import i18n from '@/lang/vue-18n';
import vue18n from '@/lang/vue-18n';
import moment from 'moment';
import PaperStatus from '@/paperStatus';
import {useToast} from 'vue-toastification';
import SuccessIcon from '@/icons/SuccessIcon.vue';
import UserRoleType from '@/userRoleType';
import ErrorIcon from '@/icons/ErrorIcon.vue';
import {
    ContestRoundDTO,
    ContestYearDTO,
    GarantProfileDTO,
    JudgeProfileDTO,
    PaperDTO,
    PaperRatingDTO,
    PaperRowDTO,
    StudentProfileDTO,
    UserDTO,
    UserPermissionDTO,
    UserRoleDTO,
    VSKProfileDTO
} from '@/types';
import {router} from '@inertiajs/vue3';
import route from 'ziggy-js';
import axios from 'axios';
import {Method} from '@inertiajs/core/types/types';
import InfoIcon from '@/icons/InfoIcon.vue';
import WarningIcon from '@/icons/WarningIcon.vue';

const toast = useToast();

export function parseDate(date: string): string {
    return moment(date, moment.ISO_8601).format('DD.MM.YYYY');
}

export function parseDateTime(date: string): string {
    return moment(date, moment.ISO_8601).format('DD.MM.YYYY HH:mm:ss');
}

export function successToast(message: string) {
    toast.success(vue18n.global.t('toast.success.' + message), {icon: SuccessIcon});
}

export function errorToast(message: string) {
    const key = 'toast.error.' + message;
    const translated = vue18n.global.te(key) ? vue18n.global.t(key) : message;
    toast.error(translated, {icon: ErrorIcon});
}

export function warnToast(message: string) {
    const key = 'toast.warn.' + message;
    const translated = vue18n.global.te(key) ? vue18n.global.t(key) : message;
    toast.warning(translated, {icon: WarningIcon});
}

export function infoToast(message: string) {
    toast.info(vue18n.global.t('toast.info.' + message), {icon: InfoIcon});
}

export function parseLevel(level: string) {
    return {
        name: i18n.global.t(`level.${level.toLowerCase()}`),
        value: level
    }
}

export function deleteUser(user: UserDTO, onFinish: () => void, redirect?: string, targetRoute?: string) {
    router.delete(route(targetRoute || 'api.users.delete', {
        user: user.id,
        redirect: redirect || ''
    }), {
        onSuccess: () => successToast('delete_user'),
        onFinish
    });
}

export async function downloadFile(routeUrl: string, method: Method = 'get', data: any = {}) {
    let response;
    if (method == 'get') {
        response = await axios.get(routeUrl, {
            responseType: 'blob',
            data: data
        });
    } else if (method == 'post') {
        response = await axios.post(routeUrl, data, {
            responseType: 'blob'
        })
    } else {
        throw Error(`Invalid method ${method}`);
    }
    const fileName = response.headers['content-disposition']
        .split(';')
        .find((header: string[]) => header.includes('filename'))
        .trim()
        .split('=')[1];
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export function onToggleProperty(name: string, event: Event) {
    router.reload({
        data: {
            [name]: (event.target as HTMLInputElement).checked,
            page: 1
        }
    });
}

export class ContestRound {
    id: number
    name: string
    papers: PaperDTO[]
    level: string
    level_name: string
    start_date: string
    end_date: string
    status: RoundStatus
    contest_year_id: number
    contest_year?: ContestYear
    data_type: string
    data: string
    is_group: boolean
    certificate_template?: any
    certificate_template_id?: number
    diploma_template?: any
    diploma_template_id?: number
    finish_list_template?: any
    finish_list_template_id?: number
    ratings_confirmed_at?: string
    expected_result_publication_date?: string
    certificates_issued_at?: string

    private constructor(dto: ContestRoundDTO) {
        this.id = dto.id;
        const level = parseLevel(dto.level);
        this.level = level.value;
        this.level_name = level.name;
        this.name = dto.name;
        this.papers = dto.papers;
        this.start_date = parseDate(dto.start_date);
        this.end_date = parseDate(dto.end_date);
        this.status = RoundStatus.parse(dto.status);
        this.contest_year_id = dto.contest_year_id;
        this.contest_year = dto.contest_year ? ContestYear.create(dto.contest_year) : undefined;
        this.data_type = dto.data_type;
        this.data = dto.data;
        this.is_group = dto.is_group || false;
        this.certificate_template = dto.certificate_template;
        this.certificate_template_id = dto.certificate_template_id;
        this.diploma_template = dto.diploma_template;
        this.diploma_template_id = dto.diploma_template_id;
        this.finish_list_template = dto.finish_list_template;
        this.finish_list_template_id = dto.finish_list_template_id;
        this.ratings_confirmed_at = dto.ratings_confirmed_at;
        this.expected_result_publication_date = dto.expected_result_publication_date ? parseDate(dto.expected_result_publication_date) : undefined;
        this.certificates_issued_at = dto.certificates_issued_at ? parseDate(dto.certificates_issued_at) : undefined;
    }

    static create(dto: ContestRoundDTO) {
        return new ContestRound(dto);
    }
}

export class ContestYear {
    id: number
    name: string
    order: number
    start_date: string
    end_date: string
    rounds: ContestRound[]
    status: RoundStatus
    certificate_template_id?: number
    certificate_template?: any
    finish_list_template?: any
    finish_list_template_id?: number

    private constructor(dto: ContestYearDTO) {
        this.id = dto.id;
        this.name = dto.name;
        this.order = dto.order;
        this.start_date = parseDate(dto.start_date);
        this.end_date = parseDate(dto.end_date);
        this.rounds = dto.rounds?.map(r => ContestRound.create(r)) || [];
        this.status = RoundStatus.parse(dto.status);
        this.certificate_template_id = dto.certificate_template_id;
        this.certificate_template = dto.certificate_template;
        this.finish_list_template_id = dto.finish_list_template_id;
        this.finish_list_template = dto.finish_list_template;
    }

    static create(dto: ContestYearDTO) {
        return new ContestYear(dto);
    }
}

export class PaperRating {
    id: number
    rating: PaperRatingBadgeData
    paper_id: number
    paper_name: string
    paper_author: string
    paper_author_school_name: string
    judge_name: string
    original: PaperRatingDTO

    private constructor(dto: PaperRatingDTO, allowCertificateIssue: boolean) {
        this.id = dto.id;
        this.rating = {
            id: dto.id,
            name: dto.rating,
            certificate_issued: !allowCertificateIssue || dto.certificate_issued_at != undefined,
            advanced: dto.advanced_at != null
        };
        this.paper_id = dto.paper.id;
        this.paper_name = dto.paper.name;
        this.paper_author = dto.paper.author.name;
        this.paper_author_school_name = dto.paper.author.student_profile.school.full_name;
        this.judge_name = dto.judge.name;
        this.original = dto;
    }

    static create(dto: PaperRatingDTO, allowCertificateIssue: boolean) {
        return new PaperRating(dto, allowCertificateIssue);
    }
}

export interface PaperRatingBadgeData {
    id: number
    name: string
    certificate_issued: boolean
    advanced: boolean
}

export class PaperRow {
    id: number
    name: string
    author_name: string
    author_school_name: string
    category: string
    status: PaperStatus
    rating?: PaperRatingBadgeData

    private constructor(dto: PaperRowDTO) {
        this.id = dto.id;
        this.name = dto.name;
        this.author_name = dto.author.name;
        this.author_school_name = dto.author?.student_profile?.school?.name;
        this.category = dto.contest_category.name;
        this.status = PaperStatus.parse(dto.status);
        this.rating = dto.rating;
    }

    static create(dto: PaperRowDTO) {
        return new PaperRow(dto);
    }
}

export class PaperFormData {
    name: string
    contest_category_id: number
    contest_year_id: number
    author_id: number
    coauthors: number[]
    required_equipment: string
    local_annotation: string
    english_annotation: string
    keywords: string
    appendix: string
    result_evaluation: string
    contests: string

    private constructor(value: PaperDTO) {
        this.name = value.name;
        this.contest_category_id = value.contest_category_id;
        this.contest_year_id = value.contest_year_id;
        this.author_id = value.author_id;
        this.appendix = value.appendix;
        this.coauthors = [];
        this.contests = value.contests;
        this.english_annotation = value.english_annotation;
        this.keywords = value.keywords;
        this.local_annotation = value.local_annotation;
        this.required_equipment = value.required_equipment;
        this.result_evaluation = value.result_evaluation;
    }

    static create(value: PaperDTO): PaperFormData {
        return new PaperFormData(value);
    }
}

export class User {
    id: number
    name: string
    first_name: string
    last_name: string
    email: string
    rolesTypes: UserRoleType[]
    roles: UserRoleDTO[]
    permissions: UserPermissionDTO[]
    created_at: string
    email_verified_at?: string

    profiles?: any[]
    student_profile?: StudentProfile
    judge_profile?: JudgeProfileDTO
    garant_profile?: GarantProfileDTO
    vsk_profile?: VSKProfileDTO

    private constructor(dto: UserDTO) {
        this.id = dto.id;
        this.name = dto.name;
        this.first_name = dto.first_name;
        this.last_name = dto.last_name;
        this.email = dto.email;
        this.profiles = dto.profiles;
        this.student_profile = dto.student_profile ? StudentProfile.create(dto.student_profile) : undefined;
        this.judge_profile = dto.judge_profile;
        this.garant_profile = dto.garant_profile;
        this.vsk_profile = dto.vsk_profile;
        this.rolesTypes = dto.roles?.map(r => UserRoleType.parse(r.slug)) || [];
        this.roles = dto.roles || [];
        this.permissions = dto.permissions || [];
        this.created_at = parseDateTime(dto.created_at);
        this.email_verified_at = dto.email_verified_at ? parseDateTime(dto.email_verified_at) : undefined;
    }

    static create(dto: UserDTO): User {
        return new User(dto);
    }

    public getInitialFormData(): { [key: string]: any } {
        return {
            ...this,
            judge_profile: this.judge_profile ? {
                contest_rounds: this.judge_profile?.contest_rounds?.map(r => r.id),
                contest_categories: this.judge_profile?.contest_categories?.map(c => c.id)
            } : undefined
        }
    }
}

export class StudentProfile {
    city: string;
    date_of_birth: string;
    grade: number;
    phone_number: string;
    school: any;
    school_id: number;
    street: string;
    zipcode: string;

    private constructor(dto: StudentProfileDTO) {
        this.city = dto.city;
        this.date_of_birth = parseDate(dto.date_of_birth);
        this.grade = dto.grade;
        this.phone_number = dto.phone_number;
        this.school = dto.school;
        this.school_id = dto.school_id;
        this.street = dto.street;
        this.zipcode = dto.zipcode;
    }

    static create(dto: StudentProfileDTO): StudentProfile {
        return new StudentProfile(dto);
    }
}

export class JudgeFormData {
    first_name: string
    last_name: string
    email: string
    judge_profile: any

    private constructor(value: UserDTO) {
        this.first_name = value.first_name;
        this.last_name = value.last_name;
        this.email = value.email;
        this.judge_profile = {
            contest_rounds: value.judge_profile?.contest_rounds?.map(r => r.id),
            contest_categories: value.judge_profile?.contest_categories?.map(c => c.id)
        }
    }

    static create(value: UserDTO): JudgeFormData {
        return new JudgeFormData(value);
    }
}

export class UserRole {
    id: number
    slug: string
    type: UserRoleType
    name: string

    private constructor(dto: UserRoleDTO) {
        this.id = dto.id;
        this.slug = dto.slug;
        this.type = UserRoleType.parse(dto.slug);
        this.name = this.type.translateName();
    }

    static create(dto: UserRoleDTO): UserRole {
        return new UserRole(dto);
    }
}

export class UserPermission {
    id: number
    slug: string
    name: string
    description: string

    private constructor(dto: UserPermissionDTO) {
        this.id = dto.id;
        this.slug = dto.slug;
        const parts = dto.slug.split('.');
        const type = parts[parts.length - 1];
        const permName = parts.slice(0, parts.length - 1);
        const normalizedSlug = dto.slug.replaceAll('.', '_');
        const self = i18n.global.te(`user_permission.${normalizedSlug}`) ? i18n.global.t(`user_permission.${normalizedSlug}`) : null;
        this.name = self || i18n.global.t(`user_permission.type.${type}`) + ' ' + i18n.global.t(`user_permission.name.${permName}`);
        const selfDesc = i18n.global.te(`user_permission.${normalizedSlug}_static`) ? i18n.global.t(`user_permission.${normalizedSlug}_static`) : null;
        this.description = i18n.global.t('user_permission.ability') + ' ' + (selfDesc || i18n.global.t(`user_permission.type_static.${type}`) + ' ' + i18n.global.t(`user_permission.name_static.${permName}`));
    }

    static create(value: UserPermissionDTO): UserPermission {
        return new UserPermission(value);
    }
}
